/**
 * @fileoverview Deep Link Context
 *
 * Stores pending deep link URL when user is not authenticated.
 * After successful login, the pending URL is processed and cleared.
 *
 * @module contexts/DeepLinkContext
 */

import { createContext, useContext, useState, ReactNode } from 'react';

interface DeepLinkContextType {
  pendingUrl: string | null;
  setPendingUrl: (url: string | null) => void;
}

const DeepLinkContext = createContext<DeepLinkContextType | undefined>(undefined);

interface DeepLinkProviderProps {
  children: ReactNode;
}

/**
 * Deep Link Provider
 *
 * Simple state container for pending deep link URLs.
 * Used when user clicks a deep link while not authenticated.
 */
export function DeepLinkProvider({ children }: DeepLinkProviderProps) {
  const [pendingUrl, setPendingUrl] = useState<string | null>(null);

  return (
    <DeepLinkContext.Provider value={{ pendingUrl, setPendingUrl }}>
      {children}
    </DeepLinkContext.Provider>
  );
}

/**
 * Hook to access deep link context
 * Must be used within DeepLinkProvider
 */
export function useDeepLinkContext() {
  const context = useContext(DeepLinkContext);
  if (context === undefined) {
    throw new Error('useDeepLinkContext must be used within a DeepLinkProvider');
  }
  return context;
}
